import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { WorkOrderSelector } from "../../utils/moduleSelectors/workOrderSelector";
import { ModernProjectActivitySelectorField } from "../../utils/moduleSelectors/ModernProjectActivitySelectorField";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import axios from "../../../api/axios";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";

export const ProjectEventDialog = ({ open, onClose, event, project, prefillDate, onSaved }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_date: "",
    end_date: "",
    days: "", // Number of days
    project_id: project?.id || null,
    work_order_id: null,
    activity_id: null,
    project_activity_id: null,
    project_activity: null,
    project: project || null,
  });
  const [resources, setResources] = useState([]); // Array of { user_id, start_time, end_time }
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (event) {
      const startDate = event.start_date ? event.start_date.substring(0, 10) : "";
      const endDate = event.end_date ? event.end_date.substring(0, 10) : "";
      let days = "";
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        days = diffDays.toString();
      }
      
      setFormData({
        title: event.title || "",
        description: event.description || "",
        start_date: startDate,
        end_date: endDate,
        days: days,
        project_id: event.project_id || project?.id || null,
        work_order_id: event.work_order_id || null,
        project: event.project || null,
      });
      // Load resources from event if available
      if (event.resources && Array.isArray(event.resources) && event.resources.length > 0) {
        setResources(event.resources.map(r => ({
          id: r.id || null, // Include id for updates
          user_id: r.user_id,
          user: r.user || (r.user_id ? { id: r.user_id, displayTitle: "Loading..." } : null),
          start_time: r.start_time ? (r.start_time.length > 16 ? r.start_time.substring(0, 16) : r.start_time) : "",
          end_time: r.end_time ? (r.end_time.length > 16 ? r.end_time.substring(0, 16) : r.end_time) : "",
        })));
      } else {
        setResources([]);
      }
    } else {
      setFormData({
        title: "",
        description: "",
        start_date: prefillDate || "",
        end_date: "",
        days: "",
        project_id: project?.id || null,
        work_order_id: null,
        activity_id: null,
        project_activity_id: null,
        project_activity: null,
        project: project || null,
      });
      setResources([]);
    }
    setErrors({});
  }, [event, project, prefillDate, open]);

  const validate = () => {
    const newErrors = {};
    if (!formData.title || formData.title.trim() === "") {
      newErrors.title = t("project.event.title_required");
    }
    if (!formData.start_date) {
      newErrors.start_date = t("project.event.start_date_required");
    }
    if (formData.end_date && formData.start_date && formData.end_date < formData.start_date) {
      newErrors.end_date = t("project.event.end_date_invalid");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addResource = () => {
    setResources([...resources, { user_id: null, user: null, start_time: "", end_time: "" }]);
  };

  const removeResource = (index) => {
    setResources(resources.filter((_, i) => i !== index));
  };

  const updateResource = (index, field, value) => {
    console.log(`updateResource called: index=${index}, field=${field}, value=`, value, "current resources:", resources);
    const updated = [...resources];
    
    // Special handling: if updating user and user_id is not set, extract it from the user object
    if (field === "user" && value && value.id && !updated[index]?.user_id) {
      updated[index] = { ...updated[index], user_id: value.id, [field]: value };
      console.log(`Auto-set user_id from user object: ${value.id}`);
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    
    console.log(`Updated resource at index ${index}:`, updated[index]);
    setResources(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setErrors({}); // Clear previous errors
    
    try {
      const url = event ? `/project_events/${event.id}` : "/project_events";
      const method = event ? "put" : "post";
      
      // Filter and format resources
      console.log("All resources before filtering:", resources); // Debug log
      
      const validResources = resources
        .filter(r => {
          // Check if user_id exists and is not null/undefined/0
          const hasUserId = r.user_id != null && r.user_id !== 0 && r.user_id !== "";
          // Check if start_time exists and is not empty string
          const hasStartTime = r.start_time != null && r.start_time !== "";
          
          const isValid = hasUserId && hasStartTime;
          
          if (!isValid) {
            console.log("Filtered out resource:", r, "Reason:", {
              hasUserId,
              hasStartTime,
              userId: r.user_id,
              startTime: r.start_time,
              endTime: r.end_time
            });
          }
          return isValid;
        })
        .map((r, index) => ({
          id: r.id || null, // Include id if it exists (for updates)
          user_id: Number(r.user_id), // Ensure it's a number
          start_time: r.start_time,
          end_time: r.end_time || r.start_time, // Default to start_time if end_time missing
        }));
      
      console.log("Valid resources after filtering:", validResources); // Debug log
      
      const payload = {
        project_event: {
          ...formData,
          project_id: formData.project_id || project?.id,
          resources: validResources,
        },
      };

      console.log("Saving project event with payload:", payload); // Debug log

      const response = await axios({ method, url, data: payload });
      if (response.data) {
        onSaved && onSaved(response.data);
        onClose(); // Only close on success
      }
    } catch (error) {
      console.error("Error saving project event:", error);
      
      // Handle different error formats
      if (error.response?.data?.errors) {
        // Rails validation errors format
        if (Array.isArray(error.response.data.errors)) {
          // Array of error messages
          const errorObj = {};
          error.response.data.errors.forEach((err, idx) => {
            errorObj[`error_${idx}`] = err;
          });
          setErrors({ ...errorObj, general: error.response.data.errors.join(", ") });
        } else {
          // Object with field names as keys
          setErrors(error.response.data.errors);
        }
      } else if (error.response?.data?.error) {
        // Single error message
        setErrors({ general: error.response.data.error });
      } else {
        // Generic error
        setErrors({ general: error.message || t("project.event.save_error") });
      }
      
      // Don't close dialog on error - let user see the errors
    } finally {
      setLoading(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={event ? t("project.event.edit") : t("project.event.create")}
      size="4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* General error message */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <p className="text-sm font-medium">{errors.general}</p>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("project.event.title")} *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.title ? "border-red-500" : "border-gray-300"
            }`}
            placeholder={t("project.event.title_placeholder")}
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600">{errors.title}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("project.event.description")}
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t("project.event.description_placeholder")}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("project.event.start_date")} *
            </label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => {
                const newStartDate = e.target.value;
                let newEndDate = formData.end_date;
                let newDays = formData.days;
                
                // If days is set, calculate end_date from start_date + days
                if (newStartDate && newDays && !isNaN(parseInt(newDays))) {
                  const start = new Date(newStartDate);
                  start.setDate(start.getDate() + parseInt(newDays));
                  newEndDate = start.toISOString().substring(0, 10);
                }
                // If end_date is set but not days, calculate days
                else if (newStartDate && newEndDate) {
                  const start = new Date(newStartDate);
                  const end = new Date(newEndDate);
                  const diffTime = Math.abs(end - start);
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  newDays = diffDays.toString();
                }
                
                setFormData({ ...formData, start_date: newStartDate, end_date: newEndDate, days: newDays });
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.start_date ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.start_date && (
              <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("project.event.days")}
            </label>
            <input
              type="number"
              min="0"
              value={formData.days}
              onChange={(e) => {
                const daysValue = e.target.value;
                let newEndDate = formData.end_date;
                
                // If start_date is set, calculate end_date from start_date + days
                if (formData.start_date && daysValue && !isNaN(parseInt(daysValue))) {
                  const start = new Date(formData.start_date);
                  start.setDate(start.getDate() + parseInt(daysValue));
                  newEndDate = start.toISOString().substring(0, 10);
                }
                
                setFormData({ ...formData, days: daysValue, end_date: newEndDate });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t("project.event.days_placeholder")}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("project.event.end_date")}
            </label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => {
                const newEndDate = e.target.value;
                let newDays = formData.days;
                
                // If start_date is set, calculate days from start_date and end_date
                if (formData.start_date && newEndDate) {
                  const start = new Date(formData.start_date);
                  const end = new Date(newEndDate);
                  const diffTime = Math.abs(end - start);
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  newDays = diffDays.toString();
                }
                
                setFormData({ ...formData, end_date: newEndDate, days: newDays });
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.end_date ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.end_date && (
              <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
            )}
          </div>
        </div>

        {!project && (
          <ProjectSelector
            name="project_id"
            label={t("project.event.project")}
            selected={formData.project ? formData?.project: null}
            onChange={(data) => {
              const newId = data.target.value;
              setFormData((prev) => ({
                ...prev,
                project_id: newId,
                project: data.target.object || null,
                ...(String(prev.project_id) !== String(newId)
                  ? {
                      work_order_id: null,
                      project_activity_id: null,
                      project_activity: null,
                    }
                  : {}),
              }));
            }}
          />
        )}

        <WorkOrderSelector
          name="work_order_id"
          label={t("project.event.work_order")}
          selected={formData.work_order_id ? { id: formData.work_order_id } : null}
          project={formData.project_id ? { id: formData.project_id } : null}
          onChange={(data) =>
            setFormData({ ...formData, work_order_id: data.target.value || null })
          }
        />

        <ModernProjectActivitySelectorField
          label={t("project_activity.label")}
          selected={formData.project_activity}
          project={
            formData.project?.id
              ? formData.project
              : formData.project_id
                ? { id: formData.project_id }
                : null
          }
          onChange={(e) => {
            setFormData((prev) => ({
              ...prev,
              project_activity_id:
                e.target.value === null || e.target.value === undefined
                  ? null
                  : e.target.value,
              project_activity: e.target.object ?? null,
            }));
          }}
        />

        {/* Resources Section */}
        <div className="pt-4 border-t">
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-gray-700">
              {t("project.event.resources")}
            </label>
            <button
              type="button"
              onClick={addResource}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              {t("project.event.add_resource")}
            </button>
          </div>

          {resources.length === 0 ? (
            <p className="text-sm text-gray-500 italic mb-4">
              {t("project.event.no_resources")}
            </p>
          ) : (
            <div className="space-y-4">
              {resources.map((resource, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700">
                      {t("project.event.resource")} {index + 1}
                    </h4>
                    <button
                      type="button"
                      onClick={() => removeResource(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <EmployeeSelector
                      name={`resource_${index}_user_id`}
                      label={t("project.event.resource_employee")}
                      selected={resource.user ? { 
                        id: resource.user_id, 
                        displayTitle: resource.user?.displayTitle || resource.user?.name 
                      } : resource.user_id ? {
                        id: resource.user_id,
                        displayTitle: "Loading..."
                      } : null}
                      onChange={(data) => {
                        console.log("EmployeeSelector onChange - full data:", data); // Debug log
                        
                        // Get userId from multiple possible locations
                        const userId = data.target.value || 
                                     data.target.object?.id || 
                                     data.target.data?.id ||
                                     data.target.object?.id;
                        
                        // Get userObject
                        const userObject = data.target.object || 
                                         data.target.data ||
                                         (userId ? { id: userId } : null);
                        
                        console.log("Extracted userId:", userId, "userObject:", userObject); // Debug log
                        
                        if (userId) {
                          updateResource(index, "user_id", userId);
                          updateResource(index, "user", userObject);
                        } else {
                          console.error("No user ID found in onChange data:", data);
                        }
                      }}
                    />

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("project.event.resource_start_time")} *
                        </label>
                        <input
                          type="datetime-local"
                          value={resource.start_time}
                          onChange={(e) => updateResource(index, "start_time", e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("project.event.resource_end_time")} *
                        </label>
                        <input
                          type="datetime-local"
                          value={resource.end_time}
                          onChange={(e) => updateResource(index, "end_time", e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {t("Cancel")}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? t("Lagrer...") : event ? t("Update") : t("Create")}
          </button>
        </div>
      </form>
    </PMDialog>
  );
};

